import { motion, useInView } from "motion/react";
import { useRef } from "react";
import { BRAND } from "../../lib/brand";
import { STACK_GROUPS } from "../../data/agencyData";

/**
 * The stack, as three static groups of chips.
 *
 * Previously two auto-scrolling marquees — which stacked three moving strips in
 * a row with the testimonials carousel and the project deck right after. Static
 * groups read faster, say what each tool is for, and let the eye rest before
 * the carousel below.
 */
export function StackSection() {
  const ref = useRef<HTMLElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section ref={ref} className="relative w-full py-16 md:py-24 px-4 md:px-[116px] flex flex-col items-center gap-8">
      <p className="font-mono text-[12px] md:text-[14px] tracking-[3px] uppercase text-[#666]">Technology</p>
      <h2 className="font-black text-[48px] md:text-[96px] leading-[1.05] tracking-[-2.4px] text-[#141414] text-center">
        Our Stack
      </h2>

      <p className="text-[16px] md:text-[20px] leading-[1.6] text-[#666] text-center max-w-[680px]">
        Tools are only half of it. Every build also runs through a hardening, AVG and WCAG checklist before it goes
        live — and you get the report that proves it.
      </p>

      <div className="w-full max-w-[1100px] mt-4 grid gap-10 md:grid-cols-3 md:gap-8">
        {STACK_GROUPS.map((group, gi) => (
          <motion.div
            key={group.title}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5, delay: gi * 0.12, ease: "easeOut" }}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1">
              <h3 className="font-bold text-[19px] md:text-[21px] leading-tight text-[#141414]">{group.title}</h3>
              <p className="text-[14px] leading-[1.5] text-[#666]">{group.note}</p>
            </div>

            <span className="h-[3px] w-[46px] rounded-full" style={{ background: BRAND.green }} aria-hidden="true" />

            <ul className="flex flex-wrap gap-2">
              {group.items.map((item) => (
                <li
                  key={item}
                  className="rounded-full px-4 py-2 font-semibold text-[13px] md:text-[15px] text-[#141414] shadow-[0px_4px_10px_-4px_rgba(0,0,0,0.25)] transition-transform duration-200 hover:-translate-y-0.5"
                  style={{ background: BRAND.yellow }}
                >
                  {item}
                </li>
              ))}
            </ul>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
